import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { createApiClient } from "@/api/client";
import { queryKeys } from "@/api/queryClient";
import type { ProcessDefinitionView } from "@/types";

function useClient() {
  const { session, logout } = useAuth();
  return () => createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
}

// #282: Scaffold + Metadaten + Aktivierung. Objekt-Urteile fällt der Master (PDP);
// die UI zeigt 403 generisch (kein zweites Regelwerk).

export interface ScaffoldProcessInput {
  id: string;
  name: string;
  description?: string;
}

export function useScaffoldProcess(): UseMutationResult<ProcessDefinitionView, Error, ScaffoldProcessInput> {
  const qc = useQueryClient();
  const getClient = useClient();
  return useMutation({
    mutationFn: (input) => getClient().post<ProcessDefinitionView>("/processes", input),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.resource("processes") }),
  });
}

export function useUpdateProcess(): UseMutationResult<
  ProcessDefinitionView,
  Error,
  { id: string; patch: { name?: string; description?: string; ownerId?: string } }
> {
  const qc = useQueryClient();
  const getClient = useClient();
  return useMutation({
    mutationFn: ({ id, patch }) => getClient().patch<ProcessDefinitionView>(`/processes/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.resource("processes") }),
  });
}

export function useDeleteProcess(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  const getClient = useClient();
  return useMutation({
    mutationFn: (id) => getClient().delete<void>(`/processes/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.resource("processes") }),
  });
}

export function useActivateProcess(): UseMutationResult<ProcessDefinitionView, Error, { id: string; version: string }> {
  const qc = useQueryClient();
  const getClient = useClient();
  return useMutation({
    mutationFn: ({ id, version }) => getClient().post<ProcessDefinitionView>(`/processes/${id}/activate`, { version }),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.resource("processes") }),
  });
}

// Nachtrag 2026-08-22: Release = Master erstellt den Versions-Tag aus der process.json.
export function useReleaseProcess(): UseMutationResult<{ version: string; commitSha: string }, Error, string> {
  const qc = useQueryClient();
  const getClient = useClient();
  return useMutation({
    mutationFn: (id) => getClient().post<{ version: string; commitSha: string }>(`/processes/${id}/release`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.resource("processes") }),
  });
}

export function usePauseProcess(): UseMutationResult<ProcessDefinitionView, Error, string> {
  const qc = useQueryClient();
  const getClient = useClient();
  return useMutation({
    mutationFn: (id) => getClient().post<ProcessDefinitionView>(`/processes/${id}/pause`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.resource("processes") }),
  });
}
